import React from 'react';
import styles from '../InfoCard/InfoCard.module.scss';

type CallTimeData = {
    totalCount: number;
};

export type TotalCallTimeProps = {
    callData?: CallTimeData;
};

/**
 * Component to display total call time calculated from call qualification data
 * @param callData - Object with totalCount representing total seconds
 * @returns A card displaying total call time in hours, minutes and seconds
 */
const TotalCallTime = ({ callData }: TotalCallTimeProps) => {
    const calculateTotalTime = () => {
        if (!callData) {
            return { hours: 0, minutes: 0, seconds: 0 };
        }

        // Convert total seconds to hours, minutes and remaining seconds
        const hours = Math.floor(callData.totalCount / 3600);
        const minutes = Math.floor((callData.totalCount % 3600) / 60);
        const seconds = Math.round(callData.totalCount % 60);

        return { hours, minutes, seconds };
    };

    const { hours, minutes, seconds } = calculateTotalTime();

    const renderTimeDisplay = () => {
        // If all are zero, show just 0 seconds
        if (hours === 0 && minutes === 0 && seconds === 0) {
            return (
                <div className={styles.timeDisplay}>
                    <div className={styles.timeUnit}>
                        <span className={styles.timeValue}>0</span>
                        <span className={styles.timeLabel}>seconds</span>
                    </div>
                </div>
            );
        }

        const timeUnits: React.ReactNode[] = [];

        if (hours > 0) {
            timeUnits.push(
                <div key='hours' className={styles.timeUnit}>
                    <span className={styles.timeValue}>{hours}</span>
                    <span className={styles.timeLabel}>hour{hours !== 1 ? 's' : ''}</span>
                </div>
            );
        }

        if (minutes > 0) {
            timeUnits.push(
                <div key='minutes' className={styles.timeUnit}>
                    <span className={styles.timeValue}>{minutes}</span>
                    <span className={styles.timeLabel}>minute{minutes !== 1 ? 's' : ''}</span>
                </div>
            );
        }

        if (seconds > 0) {
            timeUnits.push(
                <div key='seconds' className={styles.timeUnit}>
                    <span className={styles.timeValue}>{seconds}</span>
                    <span className={styles.timeLabel}>second{seconds !== 1 ? 's' : ''}</span>
                </div>
            );
        }

        return <div className={styles.timeDisplay}>{timeUnits}</div>;
    };

    return (
        <div className={styles.infoCard}>
            <h3>Total Call Time</h3>
            <div className={styles.infoValue}>{renderTimeDisplay()}</div>
        </div>
    );
};

export default TotalCallTime;
